/**
 * UsersPage - Страница управления пользователями
 * Использует компонент DataTable
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  Badge,
  Table,
  Text,
  rem,
  Loader,
  Center,
  Pagination,
  Group,
  Button,
  Menu,
  ActionIcon,
} from '@mantine/core';
import {
  IconSearch,
  IconPlus,
  IconPencil,
  IconKey,
  IconUserOff,
  IconUserCheck,
  IconDotsVertical,
} from '@tabler/icons-react';
import { ROLE_INFO } from '../../../constants/permissions';
import * as usersService from '../../../services/api/usersService';
import DataTable, { DataTableRow } from '../../common/DataTable';
import UserFormModal from './UserFormModal';
import ResetPasswordModal from './ResetPasswordModal';

// Цвета для ролей
const roleColors = {
  ADMIN: 'blue',
  MANAGER: 'cyan',
  VIEWER: 'gray',
};

// Колонки таблицы
const columns = [
  { key: 'user', label: 'Пользователь' },
  { key: 'role', label: 'Роль' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Статус' },
  { key: 'actions', label: '', sortable: false },
];

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');

  // Модальные окна
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Загрузка пользователей
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await usersService.getUsers({
        page,
        pageSize: 25,
        search: search || null,
        excludeRole: 'PARENT',
      });
      setUsers(response.items);
      setTotalPages(response.pages);
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Обработчики
  const handleCreateUser = async (data) => {
    setActionLoading(true);
    try {
      await usersService.createUser(data);
      setCreateModalOpen(false);
      loadUsers();
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateUser = async (data) => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await usersService.updateUser(selectedUser.id, data);
      setEditModalOpen(false);
      loadUsers();
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (newPassword, requireChange) => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      await usersService.resetUserPassword(selectedUser.id, newPassword, requireChange);
      setResetPasswordModalOpen(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      if (user.is_active) {
        await usersService.deactivateUser(user.id);
      } else {
        await usersService.activateUser(user.id);
      }
      loadUsers();
    } catch (err) {
      console.error('Failed to toggle user status:', err);
    }
  };

  // Получить инициалы пользователя
  const getInitials = (user) => {
    const first = user.first_name?.[0]?.toUpperCase() || '';
    const last = user.last_name?.[0]?.toUpperCase() || '';
    return first + last || user.email?.[0]?.toUpperCase() || 'U';
  };

  // Рендер ячейки
  const renderCell = (user, column) => {
    const roleInfo = ROLE_INFO[user.role] || { name: user.role };
    const roleColor = roleColors[user.role] || 'gray';
    const fullName = `${user.last_name || ''} ${user.first_name || ''}`.trim() || user.email;

    switch (column.key) {
      case 'user':
        return (
          <Group gap="sm">
            <Avatar size={40} src={user.avatar_url} radius={40}>
              {getInitials(user)}
            </Avatar>
            <div>
              <Text fz="sm" fw={500}>
                {fullName}
              </Text>
              <Text fz="xs" c="dimmed">
                {user.email}
              </Text>
            </div>
          </Group>
        );

      case 'role':
        return (
          <Badge color={roleColor} variant="light">
            {roleInfo.name}
          </Badge>
        );

      case 'email':
        return <Text fz="sm">{user.email}</Text>;

      case 'status':
        return user.is_active ? (
          <Badge color="teal" variant="light">
            Активен
          </Badge>
        ) : (
          <Badge color="gray" variant="light">
            Неактивен
          </Badge>
        );

      case 'actions':
        return (
          <Group gap={4} justify="flex-end" wrap="nowrap">
            {/* Редактировать */}
            <ActionIcon
              variant="subtle"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(user);
                setEditModalOpen(true);
              }}
              title="Редактировать"
            >
              <IconPencil style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
            </ActionIcon>

            {/* Сбросить пароль */}
            <ActionIcon
              variant="subtle"
              color="orange"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedUser(user);
                setResetPasswordModalOpen(true);
              }}
              title="Сбросить пароль"
            >
              <IconKey style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
            </ActionIcon>

            {/* Меню дополнительных действий */}
            <Menu
              transitionProps={{ transition: 'pop' }}
              withArrow
              position="bottom-end"
              withinPortal
            >
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical style={{ width: rem(18), height: rem(18) }} stroke={1.5} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {user.is_active ? (
                  <Menu.Item
                    leftSection={<IconUserOff style={{ width: rem(14), height: rem(14) }} />}
                    color="red"
                    onClick={() => handleToggleActive(user)}
                  >
                    Деактивировать
                  </Menu.Item>
                ) : (
                  <Menu.Item
                    leftSection={<IconUserCheck style={{ width: rem(14), height: rem(14) }} />}
                    color="teal"
                    onClick={() => handleToggleActive(user)}
                  >
                    Активировать
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Group>
        );

      default:
        return undefined;
    }
  };

  return (
    <>
      {/* Кнопка добавления */}
      <Group justify="flex-end" mb="md">
        <Button
          leftSection={<IconPlus style={{ width: rem(16), height: rem(16) }} />}
          onClick={() => setCreateModalOpen(true)}
        >
          Добавить пользователя
        </Button>
      </Group>

      {/* Таблица */}
      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={users}
            renderCell={renderCell}
            showSearch={true}
            sortable={false}
          />

          {/* Пагинация */}
          {totalPages > 1 && (
            <Center mt="md">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Center>
          )}
        </>
      )}

      {/* Модальные окна */}
      <UserFormModal
        opened={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateUser}
        loading={actionLoading}
      />

      <UserFormModal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUpdateUser}
        user={selectedUser}
        loading={actionLoading}
      />

      <ResetPasswordModal
        opened={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleResetPassword}
        user={selectedUser}
        loading={actionLoading}
      />
    </>
  );
};

export default UsersPage;
